import { useContext } from 'react';
import { InterfaceContext } from '../store/InterfaceProvider';
import { getTimePeriodObject, getDeathTypeObject, cdcSite } from '../store/StaticVariables';
import { generateDisplayAttribute, geometryScaleFromGeoid } from '../utils/DataTransformation';

const SnapShotText = (args) => {
    const { feature, showDetails } = args;
    const { activeViewType, activeDeathType, activeTimePeriod, activeMeasurement, activePast12Months } = useContext(InterfaceContext);
    const deathView = activeViewType === 'deaths';

    const getActiveCountAttribute = () => {
        if (activeViewType !== 'deaths') return false;
        return generateDisplayAttribute({ type: activeViewType, deathType: activeDeathType, measurement: 'count' });
    };
    const getActiveRateAttribute = () => {
        return generateDisplayAttribute({ type: activeViewType, deathType: activeDeathType, measurement: 'rate' });
    };
    const getActiveRateModeledAttribute = () => {
        return generateDisplayAttribute({ type: activeViewType, deathType: activeDeathType, measurement: 'rate', modeled: true });
    };
    const getRateIsUnAvailable = () => {
        const attr = getActiveRateAttribute();
        const rateVal = feature[attr];
        return rateVal === -888;
    };
    const getRateIsUnUnstable = () => {
        const attr = getActiveRateAttribute();
        const rateVal = feature[attr];
        return rateVal === -999;
    };
    const getFeatureRate = () => {
        const attr = getActiveRateAttribute();
        const rateVal = feature[attr];
        if (rateVal === -888) return 'No data available ';
        if (rateVal === -999) return 'Unstable Rate';
        return parseFloat(feature[attr]).toFixed(1);
    };
    const getFeatureCount = () => {
        if (!deathView) return false;
        const attr = getActiveCountAttribute();
        const countVal = feature[attr];
        return isNaN(countVal) ? countVal : parseFloat(countVal).toLocaleString();
    };
    const getFeatureModeledValue = () => {
        const attr = getActiveRateModeledAttribute();
        return feature[attr];
    };
    const getCauseString = () => {
        if (!deathView) return '';
        const deathTypeObj = getDeathTypeObject(activeDeathType);
        return deathTypeObj.causeStr;
    };
    const getAltCauseString = () => {
        if (!deathView) return '';
        const deathTypeObj = getDeathTypeObject(activeDeathType);
        return deathTypeObj.causeStrAlt;
    };
    const getIntentString = () => {
        if (!deathView) return 'Emergency Department Visits for Firearm Injuries';
        return getDeathTypeObject(activeDeathType).title;
    };
    const aboutLink = (hash, text = 'Read More') => (
        <>
            <a href={cdcSite + '/injury-violence-data/about/index.html' + hash} target="_blank">
                {text} &raquo;
            </a>
        </>
    );

    const getClarificationString = () => {
        if (!deathView) return '';
        // if (activeDeathType !== 'all') return '';
        switch (activeDeathType) {
            case 'all':
            case 'fa_deaths':
                return ' This includes any of the ways that people die from firearm injuries: homicide, suicide, unintentional shooting, legal intervention shooting, and deaths of undetermined intent.';
            case 'all_suicide':
                return ' This includes any of the ways that people die from suicide: firearm, poisoning, suffocation, falls, cut/pierce, and all other methods.';
            case 'all_homicide':
                return ' This includes any of the ways that people die from homicide: firearm, cut/pierce, suffocation, blunt force, poisoning, and all other methods.';
            case 'drug_od':
                return <>&nbsp;Unintentional and undetermined intent drug overdose deaths are presented together. {aboutLink('#')}</>;
            default:
                return '';
        }
    };

    const expandHandler = (e) => {
        document.querySelector('.toggle-grouped').classList.toggle('expanded');
        e.nativeEvent.preventDefault();
    };
    const rateIsUnstable = getRateIsUnUnstable();
    const rateIsUnavailable = getRateIsUnAvailable();
    const rateIsModeled = getFeatureModeledValue() === 1; //if rate_modeled
    const countIsSuppressed = getFeatureCount() === '1-9'; //if count === '1-9'
    const featureScale = geometryScaleFromGeoid(feature.GEOID);
    const grouped = (feature.agg_count && feature.agg_count > 1) || false;

    const str = {
        intent: getIntentString(),
        time: activeTimePeriod === 'ttm' ? activePast12Months : activeTimePeriod, //getTimePeriodObject(activeTimePeriod).label,
        timestr: activeTimePeriod === 'ttm' ? activePast12Months + ' (most recent 12 months with data available)' : activeTimePeriod, //the Past 12 months, 2022, 2021, 2020...
        count: getFeatureCount(), //1-9, 1,000, 1, 0
        countStr: getFeatureCount(),
        rate: getFeatureRate(),
        cause: getCauseString(),
        causeAlt: getAltCauseString(),
        modeled: rateIsModeled ? 'modeled ' : '',
        Modeled: rateIsModeled ? 'Modeled ' : '',
        place: '',
        expand: '',
        expandContent: '',
        inclusions: '',
        areaName: '',
        clarification: getClarificationString(),
        residents: '',
        p1: {},
        p2: {
            subject: '',
            placeWithin: ''
        },
        p3: {
            areaDescr: ''
        },
        p4: {
            chartStateRateText: ''
        }
    };

    switch (featureScale) {
        case 'us':
            str.expand = '';
            str.expandContent = '';
            str.inclusions = 'This includes any type of firearm injuries that are treated in emergency departments, like those from assault, self-harm, legal intervention, unintentional shootings, and firearm injuries with undetermined intent.';
            str.areaName = 'the U.S.';
            str.place = 'the United States';
            str.residents = 'United States residents';
            str.p2.subject = 'The U.S.';
            str.p2.placeWithin = '';
            str.p3.areaDescr = 'the U.S.';
            str.p4.chartStateRateText = '';
            break;

        case 'usstate':
            str.expand = '';
            str.expandContent = '';
            str.inclusions = '';
            str.areaName = feature.name;
            str.place = feature.name;
            str.residents = feature.name + ' residents';
            str.p2.subject = feature.name + "'s";
            str.p2.placeWithin = '';
            str.p3.areaDescr = feature.name;
            str.p4.chartStateRateText = '';
            break;

        case 'county':
            str.expand = '';
            str.expandContent = '';
            str.inclusions = '';
            str.areaName = feature.name;
            str.place = feature.name + ', ' + feature.st_name;
            str.residents = feature.name + ' residents';
            str.p2.subject = 'The';
            str.p2.placeWithin = 'for ' + feature.name + ', ' + feature.st_name;
            str.p3.areaDescr = feature.name + ', ' + feature.st_name;
            str.p4.chartStateRateText = ' and ' + feature.st_name + ' rate';
            break;

        case 'census':
            str.expand = 'full list of census tracts grouped.';
            str.expandContent = feature.name;
            str.inclusions = '';
            str.areaName = 'this area';
            str.place = grouped ? feature.agg_count + ' Grouped Census Tracts in ' + feature.st_name : 'Census Tract ' + feature.name + ' in ' + feature.st_name;
            str.residents = grouped ? 'residents of these ' + str.place : 'residents of ' + str.place;
            str.p2.subject = 'The';
            str.p2.placeWithin = grouped ? `for these ${feature.agg_count} Grouped Census Tracts in  ${feature.st_name}` : `for Census Tract ${feature.name} in ${feature.st_name}`;
            str.p3.areaDescr = '';
            str.p4.chartStateRateText = ' and ' + feature.st_name + ' rate';
            break;

        case 'zcta':
            str.expand = 'full list of ZIP codes grouped.';
            str.expandContent = feature.name;
            str.inclusions = '';
            str.areaName = 'this area';
            str.place = grouped ? feature.agg_count + ' Grouped ZIP Codes in ' + feature.st_name : 'Zip Code ' + feature.name + ' in ' + feature.st_name;
            str.residents = grouped ? 'residents of these ' + str.place : 'residents of ' + str.place;
            str.p2.subject = '';
            str.p2.placeWithin = '';
            str.p3.areaDescr = 'this area of ' + feature.st_name;
            str.p4.chartStateRateText = ' and ' + feature.st_name + ' rate';
            break;
    }
    const p1 = (
        <>
            In {str.timestr}, {str.countStr} {str.residents} died {str.cause}.{str.clarification}
        </>
    );
    //      In [time],         [count]       people died [cause]     in         the United States.
    //      In [time],         [count]       people died [cause]     in         [State].
    //      In [time],         [count]       people died [cause]     in         [County], [State].
    //      In [time],         [count]       people died [cause]     in         Census [Tracts].
    //
    const p2 = `${str.p2.subject} ${str.modeled}rate of ${str.causeAlt} ${str.p2.placeWithin}     was ${str.rate} per 100,000 people. This means that about ${str.rate} out of every 100,000 ${str.residents}    died ${str.cause}.  `;
    //          The U.S.                        rate of [intent]                                              was [rate]      per 100,000 people. This means that about [rate]      out of every 100,000 U.S. residents         died [cause].
    //          [State]’s                       rate of [intent]                                              was [rate]      per 100,000 people. This means that about [rate]      out of every 100,000 [State] residents      died [cause].
    //          The               [modeled]     rate of [intent]                    in [County], [State]      was [rate]      per 100,000 people. This means that about [rate]      out of every 100,000 [County] residents     died [cause].
    //          The               [modeled]     rate of [intent]                    in [State] Census [Tract] was [rate]      per 100,000 people. This means that about [rate]      out of every 100,000 residents in this area died [cause].
    const p3 = (
        <>
            In {str.timestr}, the {str.modeled}rate of emergency department visits for firearm injuries for {str.p3.areaDescr} was {str.rate} per 100,000 emergency department visits. This means that about {str.rate} out of every 100,000 emergency department visits by {str.residents} were for firearm injuries.  This includes any type of firearm injuries that are treated in emergency departments, like those from assault, self-harm, legal intervention, unintentional shootings, and firearm injuries with undetermined intent. 
        </>
    );
    //          In [time],         the          rate of emergency department visits for firearm injuries for the U.S.             was [rate]  per 100,000 emergency department visits. This means that about [rate]     out of every 100,000 emergency department visits by U.S. residents         were for firearm injuries. This includes any type of firearm injuries that are treated in emergency departments, like those from assault, self-harm, legal intervention, unintentional shootings, and firearm injuries with undetermined intent.
    //          In [time],         the          rate of emergency department visits for firearm injuries for [State]              was [rate]  per 100,000 emergency department visits. This means that about [rate]     out of every 100,000 emergency department visits by [State] residents      were for firearm injuries.
    //          In [time],         the [modeled]rate of emergency department visits for firearm injuries for [County], [State]    was [rate]  per 100,000 emergency department visits. This means that about [rate]     out of every 100,000 emergency department visits by [County] residents     were for firearm injuries.
    //          In [time],         the [modeled]rate of emergency department visits for firearm injuries for this area of [State] was [rate]  per 100,000 emergency department visits. This means that about [rate]     out of every 100,000 emergency department visits by residents in this area were for firearm injuries.

    const p4a = `The chart below shows how the U.S. ${activeMeasurement} has changed over time. `;
    const p4b = `The bar graph below shows ${str.areaName}’s rate compared to the U.S. rate${str.p4.chartStateRateText}. The line graph below shows how ${str.areaName}’s rate has changed over time.`;
    //           The bar graph below shows [State]        ’s rate compared to the U.S. rate.                             The line graph below shows how [State]        ’s rate has changed over time.
    //           The bar graph below shows [County]       ’s rate compared to the U.S. rate and [State] rate.            The line graph below shows how [County]       ’s rate has changed over time.
    //           The bar graph below shows this area      ’s rate compared to the U.S. rate and [State] rate.            The line graph below shows how this area      ’s rate has changed over time.

    const unstableText = (
        <>
            <strong>This data is not available.</strong> Data is not shown when a stable rate cannot be calculated due to small numbers.&nbsp;
            {aboutLink('#', 'More on Unstable Rates')}
        </>
    );
    const unaviailableText = (
        <>
            <strong>This data is not available.</strong> Data on emergency department visits for firearm injuries comes from CDC’s National Syndromic Surveillance Program (NSSP). NSSP is a collaboration among CDC, local and state health departments, hospitals, and federal, academic, and private sector partners. CDC is displaying all available data provided by states.&nbsp;
            {aboutLink('#')}
        </>
    );
    return (
        <>
            <div id="snap-titles">
                <p className="title">
                    {str.intent} for {str.place}
                </p>
                {grouped && showDetails && (
                    <div className="toggle-grouped">
                        <a href="#" onClick={(e) => expandHandler(e)}>
                            <span>{str.expand}</span>
                        </a>
                        <p className="grouped-content">{str.expandContent}</p>
                    </div>
                )}
                <p>
                    Time Period: <strong>{str.time}</strong>
                </p>
                {deathView && (
                    <p>
                        Number of Deaths: <strong>{str.countStr}</strong>
                    </p>
                )}
                <p>
                    {str.Modeled}Rate per 100,000{deathView ? ' people' : ' Emergency Department Visits'}: <strong>{str.rate}</strong>
                </p>
            </div>
            {showDetails && rateIsUnavailable && (
                <div id="snap-details">
                    <p>{unaviailableText}</p>
                </div>
            )}
            {showDetails && rateIsUnstable && (
                <div id="snap-details">
                    <p>{unstableText}</p>
                </div>
            )}
            {showDetails && !rateIsUnstable && !rateIsUnavailable && (
                <div id="snap-details">
                    {deathView && (
                        <>
                            <p>{p1}</p>
                            <p>{p2}</p>
                        </>
                    )}
                    {!deathView && (
                        <>
                            <p>{p3}</p>
                        </>
                    )}
                    {/* <p>{featureScale === 'us' ? p4a : p4b}</p> */}
                </div>
            )}
        </>
    );
};
export default SnapShotText;
